import { atom } from 'jotai'

import {
  RevenueClassification,
  RevenueRecognitionMethod
} from 'common/types/revenueRecognition.types'
import { getValidRevenueRecognitionMethods } from 'common/utils/revenueRecognitionMethod'
import { ServicePeriodField } from '../fields'
import { DEFAULT_REVENUE_CLASSIFICATION } from '../../constants'

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

export const createFieldAtom = <State, K extends keyof State>(
  sourceAtom: ReturnType<typeof atom<State>>,
  fieldName: K
) =>
  atom(
    get => get(sourceAtom)[fieldName],
    (
      _,
      set,
      newValue: State[K] extends object ? Partial<State[K]> : State[K]
    ) => {
      set(sourceAtom, prev => {
        const currentValue = prev[fieldName]

        if (isObject(currentValue) && isObject(newValue)) {
          return {
            ...prev,
            [fieldName]: { ...currentValue, ...newValue }
          }
        }

        return { ...prev, [fieldName]: newValue }
      })
    }
  )

/**
 * Ensures the selected revenue recognition method is always valid when changing fields that are dependent on it
 */
type RevRecDependentFields = {
  revenueRecognitionMethod: RevenueRecognitionMethod | undefined
  revenueClassification: RevenueClassification | undefined
  servicePeriod: ServicePeriodField
}

export const createRevRecDependentFieldAtom = <
  State extends RevRecDependentFields,
  K extends 'revenueClassification' | 'servicePeriod'
>(
  sourceAtom: ReturnType<typeof atom<State>>,
  fieldName: K
) =>
  atom(
    get => get(sourceAtom)[fieldName],
    (_, set, newValue: State[K]) => {
      set(sourceAtom, prev => {
        const newState = { ...prev, [fieldName]: newValue }
        return withValidRevRecMethod(newState)
      })
    }
  )

const withValidRevRecMethod = <State extends RevRecDependentFields>(
  formState: State
): State => {
  if (!formState.revenueRecognitionMethod) {
    return formState
  }

  const validRecognitionMethods = getValidRevenueRecognitionMethods(
    formState.revenueClassification ?? DEFAULT_REVENUE_CLASSIFICATION,
    formState.servicePeriod
      ? {
          startDate: formState.servicePeriod.start,
          endDate: formState.servicePeriod.endInclusive
        }
      : undefined
  )

  return {
    ...formState,
    revenueRecognitionMethod: validRecognitionMethods.includes(
      formState.revenueRecognitionMethod
    )
      ? formState.revenueRecognitionMethod
      : validRecognitionMethods[0]
  }
}
